import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, CreditCard, ShieldCheck, X } from 'lucide-react';
import { api, money } from '../lib/api';

const request=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});

let sdkPromise=null;
function loadPayPalSdk(url){
  if(window.paypal?.createInstance)return Promise.resolve(window.paypal);
  if(sdkPromise)return sdkPromise;
  sdkPromise=new Promise((resolve,reject)=>{
    const existing=document.querySelector('script[data-sm-paypal-v6="true"]');
    if(existing){
      existing.addEventListener('load',()=>window.paypal?.createInstance?resolve(window.paypal):reject(new Error('PayPal Card Fields did not initialise.')),{once:true});
      existing.addEventListener('error',()=>reject(new Error('PayPal Card Fields could not be loaded.')),{once:true});
      return;
    }
    const script=document.createElement('script');
    script.async=true;
    script.src=url;
    script.dataset.smPaypalV6='true';
    script.onload=()=>window.paypal?.createInstance?resolve(window.paypal):reject(new Error('PayPal Card Fields did not initialise.'));
    script.onerror=()=>reject(new Error('PayPal Card Fields could not be loaded.'));
    document.head.appendChild(script);
  }).catch(error=>{sdkPromise=null;throw error;});
  return sdkPromise;
}

export default function CustomerPayPalCardTerminal({customerId,customer,close,onComplete}){
  const[numberHost,expiryHost,cvvHost]=[useRef(null),useRef(null),useRef(null)];
  const sessionRef=useRef(null);
  const[ready,setReady]=useState(false);
  const[eligible,setEligible]=useState(true);
  const[busy,setBusy]=useState(false);
  const[error,setError]=useState('');
  const[result,setResult]=useState(null);
  const[form,setForm]=useState({
    amount:'',
    currency:'GBP',
    description:'Sousa Murray customer payment',
    streetAddress:'',
    city:'',
    postcode:'',
    countryCode:'GB',
  });

  useEffect(()=>{
    let active=true;
    async function initialise(){
      setError('');
      try{
        const token=await api('/api/paypal-operations/card-terminal/client-token');
        const paypal=await loadPayPalSdk(token.sdkUrl);
        const sdk=await paypal.createInstance({
          clientToken:token.accessToken,
          components:['card-fields'],
          pageType:'checkout',
        });
        const methods=await sdk.findEligibleMethods({currencyCode:'GBP'}).catch(()=>null);
        if(methods?.isEligible && !methods.isEligible('advanced_cards')){
          if(active){setEligible(false);setError('PayPal has not enabled Advanced Card Payments for this account or session.');}
          return;
        }
        const session=sdk.createCardFieldsOneTimePaymentSession();
        const fieldStyle={input:{fontSize:'16px',lineHeight:'24px',color:'#101828'}};
        const number=session.createCardFieldsComponent({type:'number',placeholder:'Card number',style:fieldStyle});
        const expiry=session.createCardFieldsComponent({type:'expiry',placeholder:'MM/YY',style:fieldStyle});
        const cvv=session.createCardFieldsComponent({type:'cvv',placeholder:'CVV',style:fieldStyle});
        if(!active)return;
        numberHost.current?.replaceChildren(number);
        expiryHost.current?.replaceChildren(expiry);
        cvvHost.current?.replaceChildren(cvv);
        sessionRef.current=session;
        setReady(true);
      }catch(e){
        if(active)setError(e.message||'The PayPal card terminal could not be initialised.');
      }
    }
    void initialise();
    return()=>{
      active=false;
      sessionRef.current=null;
      numberHost.current?.replaceChildren();
      expiryHost.current?.replaceChildren();
      cvvHost.current?.replaceChildren();
    };
  },[]);

  const pay=async()=>{
    const amount=Number(form.amount);
    if(!Number.isFinite(amount)||amount<=0){setError('Enter a valid payment amount.');return;}
    if(!sessionRef.current||!ready){setError('The secure PayPal card fields are not ready yet.');return;}
    setBusy(true);setError('');setResult(null);
    try{
      const created=await api('/api/paypal-operations/orders',request('POST',{
        customer_reference:customerId,
        amount_minor:Math.round(amount*100),
        currency:form.currency,
        description:form.description,
      }));
      const orderId=created?.order?.id;
      if(!orderId)throw new Error('PayPal did not return an order ID.');

      const billingAddress={};
      if(form.postcode.trim())billingAddress.postalCode=form.postcode.trim();
      if(form.streetAddress.trim())billingAddress.streetAddress=form.streetAddress.trim();
      if(form.city.trim())billingAddress.city=form.city.trim();
      if(form.countryCode.trim())billingAddress.countryCode=form.countryCode.trim().toUpperCase();

      const submitted=await sessionRef.current.submit(orderId,{billingAddress});
      if(submitted?.state==='canceled')throw new Error('Card authentication was cancelled. The payment was not captured.');
      if(submitted?.state!=='succeeded')throw new Error(submitted?.data?.message||'PayPal could not approve the card payment.');

      const captureOrderId=submitted?.data?.orderId||orderId;
      const captured=await api('/api/paypal-operations/orders/'+encodeURIComponent(captureOrderId)+'/capture',request('POST',{}));
      const capture=captured?.capture;
      setResult({
        orderId:captureOrderId,
        captureId:capture?.id||'',
        amountMinor:capture?.amount?.value?Math.round(Number(capture.amount.value)*100):Math.round(amount*100),
        currency:capture?.amount?.currency_code||form.currency,
        status:capture?.status||captured?.order?.status||'COMPLETED',
      });
      onComplete?.(captured);
    }catch(e){
      setError(e.message||'The card payment could not be completed.');
    }finally{setBusy(false);}
  };

  return <div className="stripe-editor-backdrop" role="presentation">
    <aside className="stripe-editor c360-card-terminal" role="dialog" aria-modal="true" aria-label="Customer360 card terminal">
      <header>
        <div><span className="record-kicker">PayPal · Customer360</span><h2 className="govuk-heading-m">Take card payment</h2><p className="govuk-body">{customer?.display_name||customer?.customer_number||'Customer'}</p></div>
        <button className="icon-button" onClick={close} disabled={busy}><X size={19}/></button>
      </header>

      {result?<section className="c360-terminal-success">
        <CheckCircle2 size={34}/>
        <div><h3 className="govuk-heading-s">Payment completed</h3><p className="govuk-body">{money(result.amountMinor,result.currency)} was captured through PayPal and linked to this Customer360 account.</p></div>
        <dl><div><dt>PayPal order</dt><dd>{result.orderId}</dd></div><div><dt>Capture</dt><dd>{result.captureId||'Recorded'}</dd></div><div><dt>Status</dt><dd>{result.status}</dd></div></dl>
        <button className="button primary govuk-button" onClick={close}>Done</button>
      </section>:<>
        <section className="stripe-editor-section c360-terminal-order">
          <div><h3 className="govuk-heading-s">Payment</h3><p className="govuk-body">Set the amount and reference before the secure card fields are submitted to PayPal.</p></div>
          <div className="stripe-editor-fields">
            <label className="stripe-field govuk-label"><span>Amount</span><input className="stripe-field-control govuk-input" type="number" min="0.01" step="0.01" required value={form.amount} onChange={e=>setForm(v=>({...v,amount:e.target.value}))}/></label>
            <label className="stripe-field govuk-label"><span>Currency</span><input className="stripe-field-control govuk-input" maxLength={3} value={form.currency} onChange={e=>setForm(v=>({...v,currency:e.target.value.toUpperCase()}))}/></label>
            <label className="stripe-field wide govuk-label"><span>Description</span><input className="stripe-field-control govuk-input" value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))}/></label>
          </div>
        </section>

        <section className="stripe-editor-section">
          <div className="c360-terminal-secure-heading"><div><h3 className="govuk-heading-s">Card details</h3><p className="govuk-body">These fields are hosted and processed by PayPal. Customer360 cannot read the card number, expiry or security code.</p></div><span><ShieldCheck size={15}/>Secured by PayPal</span></div>
          {!ready&&!error&&<div className="c360-terminal-loading">Loading secure PayPal card fields…</div>}
          {error&&<div className="operation-notice error">{error}</div>}
          <div className={'c360-paypal-card-fields '+(!ready?'is-loading':'')}>
            <label className="govuk-label"><span>Card number</span><div ref={numberHost} className="c360-paypal-hosted-field" aria-label="PayPal hosted card number field"/></label>
            <div className="c360-paypal-card-row">
              <label className="govuk-label"><span>Expiry</span><div ref={expiryHost} className="c360-paypal-hosted-field" aria-label="PayPal hosted expiry field"/></label>
              <label className="govuk-label"><span>Security code</span><div ref={cvvHost} className="c360-paypal-hosted-field" aria-label="PayPal hosted security code field"/></label>
            </div>
          </div>
        </section>

        <section className="stripe-editor-section">
          <div><h3 className="govuk-heading-s">Billing address</h3><p className="govuk-body">Used for card checks and Strong Customer Authentication where required.</p></div>
          <div className="stripe-editor-fields">
            <label className="stripe-field wide govuk-label"><span>Address</span><input className="stripe-field-control govuk-input" autoComplete="street-address" value={form.streetAddress} onChange={e=>setForm(v=>({...v,streetAddress:e.target.value}))}/></label>
            <label className="stripe-field govuk-label"><span>Town / city</span><input className="stripe-field-control govuk-input" autoComplete="address-level2" value={form.city} onChange={e=>setForm(v=>({...v,city:e.target.value}))}/></label>
            <label className="stripe-field govuk-label"><span>Postcode</span><input className="stripe-field-control govuk-input" autoComplete="postal-code" value={form.postcode} onChange={e=>setForm(v=>({...v,postcode:e.target.value.toUpperCase()}))}/></label>
            <label className="stripe-field govuk-label"><span>Country</span><input className="stripe-field-control govuk-input" maxLength={2} value={form.countryCode} onChange={e=>setForm(v=>({...v,countryCode:e.target.value.toUpperCase()}))}/></label>
          </div>
        </section>

        <div className="stripe-editor-save c360-terminal-submit">
          <span><ShieldCheck size={14}/>3-D Secure will open automatically when PayPal or the card issuer requires it.</span>
          <button className="button primary govuk-button" type="button" disabled={busy||!ready||!eligible} onClick={pay}><CreditCard size={15}/>{busy?'Processing securely…':'Take payment'}</button>
        </div>
      </>}
    </aside>
  </div>;
}
